/**
 * ProfileContext.jsx
 * React Context für User-Profile Management
 *
 * Lädt automatisch das Profil nach Login und stellt es App-weit bereit.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth, isAuthEnabled } from '../auth';
import { getProfile, updateProfile as updateProfileAPI, getDeckDesigns } from '../services/profileService';
import socketService from '../socketService';

const ProfileContext = createContext(null);

export function ProfileProvider({ children }) {
  const { isLoggedIn, getAuthToken, user } = useAuth();

  const [profile, setProfile] = useState(null);
  const [deckDesigns, setDeckDesigns] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [loadAttempted, setLoadAttempted] = useState(false); // Prevent retry loops

  // Profil laden nach Login
  useEffect(() => {
    // Auth deaktiviert - nichts tun
    if (!isAuthEnabled) {
      return;
    }

    // Nicht eingeloggt - Profil zurücksetzen
    if (!isLoggedIn) {
      if (profile) {
        console.log('[Profile] Logged out, clearing profile');
        setProfile(null);
        setProfileLoaded(false);
        setLoadAttempted(false);
      }
      return;
    }

    // Bereits geladen oder bereits versucht - nicht erneut laden
    if (profileLoaded && profile) {
      return;
    }
    if (loadAttempted) {
      return; // Prevent retry loops on error
    }

    const userId = user?.id;

    const loadProfile = async () => {
      setIsLoading(true);
      setError(null);
      setLoadAttempted(true); // Mark as attempted to prevent retry loops

      try {
        // getAuthToken ist async - await nutzen
        let token = null;
        try {
          token = typeof getAuthToken === 'function' ? await getAuthToken() : null;
        } catch (tokenErr) {
          console.warn('[Profile] getAuthToken error:', tokenErr.message);
        }

        if (token) {
          const profileData = await getProfile(token);
          setProfile(profileData);
          setProfileLoaded(true);
          console.log('[Profile] Loaded:', profileData.nickname, 'deck:', profileData.deck_design);
        } else if (userId) {
          // Token nicht verfügbar - warte und versuche erneut
          console.warn('[Profile] No JWT available for user:', userId, '- will retry');
          // Reset loadAttempted damit ein erneuter Versuch möglich ist
          setLoadAttempted(false);
        }
      } catch (err) {
        console.error('[Profile] Error loading profile:', err.message);
        setError(err.message);
        // Bei Fehler: NICHT Fallback erstellen - würde echte Daten überschreiben
        // Stattdessen Error-State anzeigen und Retry ermöglichen
      } finally {
        setIsLoading(false);
      }
    };

    // Nur laden wenn User vorhanden
    if (userId || getAuthToken) {
      loadProfile();
    }
  }, [isLoggedIn, getAuthToken, user, profile, profileLoaded, loadAttempted]);

  // Deck Designs laden (einmalig)
  useEffect(() => {
    const loadDesigns = async () => {
      try {
        const designs = await getDeckDesigns();
        setDeckDesigns(designs);
      } catch (err) {
        console.error('[Profile] Error loading deck designs:', err);
      }
    };

    loadDesigns();
  }, []);

  // Profil aktualisieren
  const updateProfile = useCallback(async (updates) => {
    if (!isAuthEnabled || !getAuthToken) {
      throw new Error('Auth not enabled');
    }

    setIsLoading(true);
    setError(null);

    try {
      const token = typeof getAuthToken === 'function' ? await getAuthToken() : getAuthToken;
      const updatedProfile = await updateProfileAPI(token, updates);
      setProfile(updatedProfile);
      console.log('[Profile] Updated profile:', updates);

      // Notify server if card back changed (for live-update during game)
      if ('card_back_image' in updates || 'card_back_color' in updates) {
        socketService.emit('updateCardBack');
      }

      return updatedProfile;
    } catch (err) {
      console.error('[Profile] Error updating profile:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [getAuthToken]);

  // Nickname ändern
  const setNickname = useCallback((nickname) => {
    return updateProfile({ nickname });
  }, [updateProfile]);

  // Deck Design ändern
  const setDeckDesign = useCallback((deck_design) => {
    return updateProfile({ deck_design });
  }, [updateProfile]);

  // Card Back Color ändern
  const setCardBackColor = useCallback((card_back_color) => {
    return updateProfile({ card_back_color });
  }, [updateProfile]);

  // Card Back Image ändern (Base64 Data URL oder null zum Löschen)
  const setCardBackImage = useCallback((card_back_image) => {
    return updateProfile({ card_back_image });
  }, [updateProfile]);

  // Profile Image ändern (Base64 Data URL oder null zum Löschen)
  const setProfileImage = useCallback((profile_image) => {
    return updateProfile({ profile_image });
  }, [updateProfile]);

  // Note: Card Back CSS wird zentral von useHostCardBack in App.jsx verwaltet

  const value = {
    // State
    profile,
    deckDesigns,
    isLoading,
    error,

    // Computed
    userId: profile?.id || null, // Dynamic.xyz User ID for stats tracking
    nickname: profile?.nickname || null,
    deckDesign: profile?.deck_design || 'classic',
    cardBackColor: profile?.card_back_color || 'classic',
    cardBackImage: profile?.card_back_image || null,
    profileImage: profile?.profile_image || null,
    gamesPlayed: profile?.games_played || 0,
    gamesWon: profile?.games_won || 0,

    // Actions
    updateProfile,
    setNickname,
    setDeckDesign,
    setCardBackColor,
    setCardBackImage,
    setProfileImage,
    refreshProfile: () => {
      // Trigger re-fetch by resetting all states
      setProfile(null);
      setProfileLoaded(false);
      setLoadAttempted(false);
    },
  };

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
}

export function useProfile() {
  const context = useContext(ProfileContext);
  if (!context) {
    // Fallback wenn Provider nicht vorhanden
    return {
      profile: null,
      deckDesigns: [],
      isLoading: false,
      error: null,
      userId: null,
      nickname: null,
      deckDesign: 'classic',
      cardBackColor: 'classic',
      cardBackImage: null,
      profileImage: null,
      gamesPlayed: 0,
      gamesWon: 0,
      updateProfile: () => Promise.resolve(),
      setNickname: () => Promise.resolve(),
      setDeckDesign: () => Promise.resolve(),
      setCardBackColor: () => Promise.resolve(),
      setCardBackImage: () => Promise.resolve(),
      setProfileImage: () => Promise.resolve(),
      refreshProfile: () => {},
    };
  }
  return context;
}

export default ProfileContext;
